// Reception dashboard — fixtures (RON-5469).
//
// ⚠ THESE ARE IN THE API'S OWN SHAPE (snake_case, nested), not the module's.
//
// That is deliberate and it differs from `visitorMockData.js`, which holds already-flattened rows.
// Keeping the fixtures in wire format means `receptionApiMapper.js` runs on every mocked read, so
// the mapping is exercised in the browser during design review rather than only in its unit test.
// When `USE_RECEPTION_MOCKS` goes false, nothing downstream changes shape.
//
// Names and companies mirror the prototype's board so a side-by-side review lines up.

export const USE_RECEPTION_MOCKS = false;

// §2 — every call is scoped to one site, and the switcher is one-at-a-time.
// `reception_pin_set` decides whether the lock screen draws a PIN box at all (§7b).
export const MOCK_SITES = [
  {
    id: "site-dublin",
    name: "Northwall Quay HQ",
    address: "North Wall Quay, Dublin 1",
    timezone: "Europe/Dublin",
    location: { lat: 53.3478, lng: -6.2297 },
    reception_pin_set: true,
  },
  {
    id: "site-cork",
    name: "Cork Office",
    address: "Lapps Quay, Cork",
    timezone: "Europe/Dublin",
    location: { lat: 51.8969, lng: -8.4693 },
    reception_pin_set: false,
  },
  {
    id: "site-london",
    name: "London Office",
    address: "10 Bishopsgate, London EC2",
    timezone: "Europe/London",
    location: { lat: 51.5155, lng: -0.081 },
    reception_pin_set: true,
  },
];

// The board's day. Fixed rather than "today" so a screenshot taken next month still shows a full
// board — the same reason the prototype pins 28 Apr 2026.
export const MOCK_TODAY = "2026-04-28";

// The PIN the mock's unlock endpoint accepts (§7b).
//
// It lives HERE, in the fixtures, and not in the lock component — which is the whole point of the
// exercise. The real secret is held server-side precisely because the browser at a locked desk
// still has a valid session, so a check written in the client is one a passer-by skips by
// reloading the page. When `USE_RECEPTION_MOCKS` goes false this constant is deleted and nothing
// on the client ever knows a PIN.
export const MOCK_RECEPTION_PIN = "4821";

const row = (overrides) => ({
  booking_code: "YTJ6A2L3",
  company: null,
  email: null,
  kind: "visitor",
  is_vip: false,
  is_walk_in: false,
  status: "expected",
  is_overstay: false,
  overstay_by: null,
  building: null,
  purpose: null,
  start_date: MOCK_TODAY,
  end_date: MOCK_TODAY,
  arrival_time: "09:00:00",
  expected_out: null,
  checked_in_at: null,
  checked_out_at: null,
  personnel: 1,
  contractor: null,
  // Only the two keys the kiosk actually writes. `hs_acknowledged` and `nda_signed` are absent
  // everywhere in these fixtures ON PURPOSE — see the mapper's note. A fixture that invented them
  // would hide the "Not recorded yet" state from every screenshot.
  compliance: { photo_captured: false, badge_number: null, badge_state: null },
  can_check_in: true,
  can_check_out: false,
  ...overrides,
});

export const MOCK_ROWS = [
  row({
    id: "r-sarah",
    name: "Sarah Mitchell",
    company: "Acme Corp",
    email: "s.mitchell@acmecorp.com",
    host: "Michael Walsh",
    purpose: "Quarterly board review",
    status: "checked_in",
    arrival_time: "09:30:00",
    expected_out: "17:00:00",
    checked_in_at: `${MOCK_TODAY} 09:30:12`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-001", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  row({
    id: "r-jane",
    name: "Jane Lawler",
    company: "Bank of Ireland",
    email: "j.lawler@boi.example",
    host: "Áine Howard",
    purpose: "CEO briefing",
    status: "checked_in",
    is_vip: true,
    arrival_time: "10:00:00",
    expected_out: "12:30:00",
    checked_in_at: `${MOCK_TODAY} 09:58:40`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-002", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  row({
    id: "r-patrick",
    name: "Patrick Kelly",
    company: "Department of Finance",
    host: "Áine Howard",
    status: "checked_in",
    arrival_time: "10:15:00",
    expected_out: "16:00:00",
    checked_in_at: `${MOCK_TODAY} 10:14:02`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-003", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  row({
    id: "r-john",
    name: "John Doyle",
    company: null,
    host: "David Murray",
    purpose: "Parcel drop",
    status: "checked_in",
    is_walk_in: true,
    arrival_time: "09:45:00",
    checked_in_at: `${MOCK_TODAY} 09:45:31`,
    compliance: { photo_captured: false, badge_number: "V-2026-0428-004", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  // THE OVERSTAY. Still `checked_in` — overstay is a flag, never a status, and the person stays
  // on the board and on the muster until check-out is confirmed.
  row({
    id: "r-tom",
    name: "Tom Jenkins",
    company: "Lamb Lawrence",
    host: "Michael Walsh",
    status: "checked_in",
    is_overstay: true,
    overstay_by: 74,
    arrival_time: "08:00:00",
    expected_out: "13:00:00",
    checked_in_at: `${MOCK_TODAY} 07:58:19`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-005", badge_state: "active" },
    can_check_in: false,
    can_check_out: true,
  }),
  row({
    id: "r-mark",
    name: "Mark Phillips",
    host: "Sarah Casey",
    status: "checked_in",
    is_walk_in: true,
    arrival_time: "11:20:00",
    checked_in_at: `${MOCK_TODAY} 11:20:44`,
    can_check_in: false,
    can_check_out: true,
  }),
  // A contractor. `purpose` carries the work description for this kind.
  row({
    id: "r-declan",
    name: "Declan Murphy",
    company: "Murphy Electrical",
    email: "declan@murphyelectrical.ie",
    kind: "contractor",
    host: "Michael Walsh",
    purpose: "Rewiring — second floor plant room",
    arrival_time: "13:00:00",
    expected_out: "17:30:00",
    personnel: 3,
    contractor: {
      id: "c-declan",
      trade: "Electrical",
      status: "vetted",
      vetted_until: "2027-06-30",
      is_provisional: false,
    },
  }),
  // A PROVISIONAL contractor — check-in must be refused with the reason in words (§6).
  row({
    id: "r-lisa",
    name: "Lisa Murphy",
    company: "EY",
    kind: "contractor",
    host: "Michael Walsh",
    purpose: "Audit fieldwork",
    arrival_time: "14:00:00",
    personnel: 2,
    contractor: {
      id: "c-lisa",
      trade: "Audit",
      status: "waiting",
      vetted_until: null,
      is_provisional: true,
    },
    can_check_in: false,
  }),
  row({
    id: "r-robert",
    name: "Robert Cunningham",
    company: "Department of Finance",
    host: "Áine Howard",
    is_vip: true,
    arrival_time: "15:00:00",
    expected_out: "17:00:00",
  }),
  row({
    id: "r-aoife",
    name: "Aoife O'Brien",
    company: "EY",
    host: "Sarah Casey",
    arrival_time: "15:30:00",
  }),
  row({
    id: "r-karol",
    name: "Karol Nowak",
    company: "IBM",
    host: "Liam Byrne",
    arrival_time: "16:00:00",
  }),
  // Already gone — `checked_out` rows stay on the day's board.
  row({
    id: "r-priya",
    name: "Priya Sharma",
    company: "Nova Tech",
    host: "Liam Byrne",
    status: "checked_out",
    arrival_time: "09:00:00",
    expected_out: "11:00:00",
    checked_in_at: `${MOCK_TODAY} 08:57:10`,
    checked_out_at: `${MOCK_TODAY} 11:04:55`,
    compliance: { photo_captured: true, badge_number: "V-2026-0428-006", badge_state: "returned" },
    can_check_in: false,
    can_check_out: false,
  }),
];

// A different day, so the date arrows have somewhere to go and the "Expected" tab can be seen on
// its own. Yesterday is all closed out; tomorrow is all still expected.
export const MOCK_ROWS_BY_DATE = {
  [MOCK_TODAY]: MOCK_ROWS,
  "2026-04-27": [
    row({
      id: "r-david",
      name: "David Chen",
      company: "Microsoft",
      host: "Liam Byrne",
      status: "checked_out",
      start_date: "2026-04-27",
      end_date: "2026-04-27",
      arrival_time: "10:00:00",
      checked_in_at: "2026-04-27 09:58:00",
      checked_out_at: "2026-04-27 15:30:00",
      can_check_in: false,
      can_check_out: false,
    }),
    row({
      id: "r-maria",
      name: "Maria Rodriguez",
      company: "BNP Paribas",
      host: "Áine Howard",
      is_vip: true,
      status: "checked_out",
      start_date: "2026-04-27",
      end_date: "2026-04-27",
      arrival_time: "11:00:00",
      checked_in_at: "2026-04-27 10:55:00",
      checked_out_at: "2026-04-27 14:00:00",
      can_check_in: false,
      can_check_out: false,
    }),
  ],
  "2026-04-29": [
    row({
      id: "r-hassan",
      name: "Hassan Al-Rahman",
      company: "Goldman Sachs",
      host: "Áine Howard",
      is_vip: true,
      start_date: "2026-04-29",
      end_date: "2026-04-29",
      arrival_time: "09:30:00",
    }),
    row({
      id: "r-niall",
      name: "Niall Burke",
      company: "Deloitte",
      host: "Liam Byrne",
      start_date: "2026-04-29",
      end_date: "2026-04-29",
      arrival_time: "14:00:00",
    }),
  ],
};

// §5 — the walk-in dialog's host picker. Every ACTIVE EMPLOYEE, not only designated hosts: a
// visitor asks for a person by name and that person need not hold the visitor capability.
// `is_host` marks the designated ones so the screen can sort or badge them.
export const MOCK_HOSTS = [
  { id: "h-michael", name: "Michael Walsh", email: "michael.walsh@example.ie", is_host: true },
  { id: "h-sarah", name: "Sarah Casey", email: "sarah.casey@example.ie", is_host: true },
  { id: "h-liam", name: "Liam Byrne", email: "liam.byrne@example.ie", is_host: true },
  { id: "h-aine", name: "Áine Howard", email: "aine.howard@example.ie", is_host: true },
  { id: "h-david", name: "David Murray", email: "david.murray@example.ie", is_host: false },
];
